import React, { useMemo, useRef } from 'react'
import { Link } from 'react-router-dom'
import { useQueryParam } from '../shared/hooks/location'
import { AuthHead, AuthMark, AuthPage } from '../shared/sso-workspace/auth-card'
import { classifyOpenError } from './open-error-copy'
import { useCopy } from '../shared/clipboard/clipboard'

// /open/error: a single sign-on that failed before the connection was even
// reached (an unknown or turned-off connection, an expired sign-in link, or
// the identity provider rejecting EducationXR's own credential). The auth
// service sends its raw error text base64-encoded in ?error= -- there's no
// reason code the way /auth/error gets one (login-error.js), so
// open-error-copy.js pattern-matches the decoded text into the right
// message. The raw text itself is never shown up front: it's staff-facing
// detail, not something a user needs to read, so it sits behind the
// Technical details disclosure below, closed by default.
const decode = value => {
  try {
    return value ? atob(value) : ''
  } catch {
    return ''
  }
}

export const OpenError = () => {
  const errMsg = useQueryParam('error')

  const error = useMemo(() => decode(errMsg), [errMsg])
  const copy = useMemo(() => classifyOpenError(error), [error])

  return (
    <AuthPage labelledBy="open-error-title">
      <AuthHead id="open-error-title" title={copy.title} mark={<AuthMark tone="bad">!</AuthMark>} />
      <p className="m-0">{copy.body}</p>

      {copy.key === 'credential'
        ? (
          <div className="sa-actions">
            <Link replace className="btn sw-btn-primary sa-block" to="/auth/email?use=password">Sign in with password</Link>
            <Link replace className="btn sw-btn sa-block" to="/auth/email">Back to sign in</Link>
          </div>
          )
        : <Link replace className="btn sw-btn-primary sa-block" to="/auth/email">Back to sign in</Link>}

      {error && <TechnicalDetails message={error} />}
    </AuthPage>
  )
}

// Staff need the raw error (it's what support matches to the auth service's
// own log row); the person who hit this page doesn't. A native
// <details>/<summary> keeps it closed by default with no state of its own,
// and is keyboard- and screen-reader-operable for free. The message renders
// in a wrapped, monospaced block -- overflow-wrap: anywhere (sa-tech-msg,
// auth-card.scss) is what keeps a long unbroken token (a JWT, a GUID) from
// pushing the box past the card's edge the way the old plain <p> did.
// Copy goes through the shared helper (shared/clipboard): Copied only after a
// real copy; otherwise the message is selected and the shortcut named.
const TechnicalDetails = ({ message }) => {
  const copy = useCopy()
  const text = useRef(null)

  return (
    <details className="sa-tech">
      <summary>Technical details</summary>
      <div className="sa-tech-body">
        <p className="sa-tech-msg sw-mono" ref={text}>{message}</p>
        <button type="button" className="btn sw-btn sa-tech-copy" onClick={() => copy.copy(message, text)}>{copy.copied ? 'Copied' : 'Copy'}</button>
        {copy.manual && <p className="sw-hint m-0" role="status">{copy.hint}</p>}
      </div>
    </details>
  )
}
